
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Lock, CheckCircle, XCircle, ArrowLeft, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { STRIPE_TEST_MODE } from "@/lib/utils";
import { Capacitor } from '@capacitor/core';
import { openPaymentUrl, saveNativePaymentSession } from '@/utils/nativePayment';

interface PaymentData {
  eventId: number;
  mode: 'individual' | 'parejas';
  partner?: any;
  paymentMode: 'my_part' | 'full_amount';
  amount: number;
}

interface PaymentProcessProps {
  paymentData: PaymentData;
  eventTitle: string;
  eventData?: any;
  registrationData?: any;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
  onBack: () => void;
}

const PaymentProcess = ({ 
  paymentData, 
  eventTitle, 
  eventData,
  registrationData,
  onPaymentSuccess, 
  onPaymentError, 
  onBack 
}: PaymentProcessProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayment = async () => {
    setIsProcessing(true);
    
    try {
      console.log("Creating Stripe checkout session...");
      
      // Call the edge function to create a Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: {
          paymentData,
          eventTitle,
          registrationData: {
            ...registrationData,
            isNative: Capacitor.isNativePlatform(),
          },
          testMode: STRIPE_TEST_MODE
        }
      });

      if (error) {
        throw new Error(error.message || 'Error al crear la sesión de pago');
      }

      if (!data?.url) {
        throw new Error('No se recibió la URL de pago');
      }

      console.log('Redirecting to Stripe checkout:', data.url);

      if (Capacitor.isNativePlatform()) {
        // Native: open in-app browser, wait for close, then verify payment
        if (data.sessionId) {
          saveNativePaymentSession(data.sessionId, paymentData.eventId.toString());
        }
        toast.success('Abriendo página de pago...');

        const { closedByUser } = await openPaymentUrl(data.url);

        if (closedByUser && data.sessionId) {
          // User closed the in-app browser — navigate to verify payment
          // Don't set payment_success=true yet — let verification determine the result
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.set('session_id', data.sessionId);
          currentUrl.searchParams.set('payment_pending', 'true');
          currentUrl.searchParams.set('step', '3');
          window.location.href = currentUrl.toString();
          return;
        }
      } else {
        // Web: existing behavior
        toast.success('Abriendo página de pago de Stripe...');
        await new Promise(resolve => setTimeout(resolve, 500));

        const stripeWindow = window.open(data.url, '_blank');

        if (!stripeWindow) {
          console.log('Popup blocked, redirecting in same window');
          window.location.href = data.url;
          return;
        }
      }
      
    } catch (error) {
      console.error("Payment creation failed:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al procesar el pago";
      toast.error(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      // Always reset processing state except when redirecting in same window
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Proceso de Pago</h1>
          <p className="text-gray-600 mt-1">Completa tu inscripción</p>
        </div>
        <Button variant="outline" onClick={onBack} disabled={isProcessing}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>

      {/* Payment Summary */}
      <Card className="bg-white/80 backdrop-blur-sm border-primary/20">
        <CardHeader>
          <CardTitle>Resumen del Pago</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Event Title */}
          <div className="text-center border-b pb-4">
            <h3 className="font-bold text-xl text-gray-900">{eventTitle}</h3>
          </div>

          {/* Event Details */}
          <div className="space-y-4">
            {/* Participants Section */}
            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <h4 className="font-semibold text-primary text-sm mb-3 flex items-center gap-2">
                <span>👥</span> Participantes
              </h4>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <span>👤</span> {paymentData.partner?.userName || 'Usuario Actual'}
                </p>
                {paymentData.mode === 'parejas' && paymentData.partner && (
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <span>🤝</span> <span className="text-primary">Pareja:</span> {paymentData.partner.name}
                  </p>
                )}
              </div>
            </div>

            {/* Event Info Section */}
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-semibold text-green-900 text-sm mb-3 flex items-center gap-2">
                <span>📋</span> Información del Evento
              </h4>
              {eventData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <span>📍</span>
                    <span className="font-medium">Ubicación:</span>
                    <span>{eventData.location}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-700">
                    <span>📅</span>
                    <span className="font-medium">Fecha:</span>
                    <span>
                      {eventData.isLeague && eventData.endDate && eventData.endDate !== eventData.date ? 
                        `${new Date(eventData.startDate || eventData.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - ${new Date(eventData.endDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}` :
                        new Date(eventData.startDate || eventData.date).toLocaleDateString('es-ES', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-700">
                    <span>🕐</span>
                    <span className="font-medium">Hora:</span>
                    <span>{eventData.time}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-700">
                    <span>🏸</span>
                    <span className="font-medium">Deporte:</span>
                    <span>{eventData.sport} - {eventData.matchType === 'singles' ? 'Individual' : 'Parejas'}</span>
                  </div>
                  
                  {/* Format Badge */}
                  <div className="flex items-center gap-2 text-gray-700 md:col-span-2">
                    <span>🎯</span>
                    <span className="font-medium">Formato:</span>
                    <Badge variant="outline" className="text-xs">
                      {eventData.isLeague ? (
                        eventData.leagueFormat === 'round-robin' ? 'Round Robin' :
                        eventData.leagueFormat === 'sumatoria-juegos' ? 'Sumatoria de Juegos' :
                        eventData.leagueFormat === 'escalera-piramidal' ? 'Escalera (Formato Piramidal)' :
                        'Liga'
                      ) : (
                        eventData.tournamentFormat === 'round-robin' ? 'Round Robin' :
                        eventData.tournamentFormat === 'eliminacion-directa' ? 'Eliminación Directa' :
                        eventData.tournamentFormat === 'sumatoria-juegos' ? 'Sumatoria de Juegos' :
                        'Torneo'
                      )}
                    </Badge>
                  </div>
                  
                  {/* Category and Subcategory */}
                  {eventData.category && eventData.subcategory && (
                    <div className="flex items-center gap-2 text-gray-700 md:col-span-2">
                      <span>🏆</span>
                      <span className="font-medium">Categoría:</span>
                      <span>{eventData.category} - {eventData.subcategory}</span>
                    </div>
                  )}
                  
                  {/* Organizer */}
                  {eventData.organizerName && (
                    <div className="flex items-center gap-2 text-gray-700 md:col-span-2">
                      <span>👨‍💼</span>
                      <span className="font-medium">Organizador:</span>
                      <span>{eventData.organizerName}</span>
                    </div>
                  )}
                  
                  {/* Registration Deadline */}
                  {eventData.registrationDeadline && (
                    <div className="flex items-center gap-2 text-gray-700 md:col-span-2">
                      <span>⏰</span>
                      <span className="font-medium">Fecha límite:</span>
                      <span>{new Date(eventData.registrationDeadline).toLocaleDateString('es-ES', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}</span>
                    </div>
                  )}
                  
                  {/* Prize info */}
                  {eventData.prizes && eventData.prizes.length > 0 && (
                    <div className="flex items-start gap-2 text-gray-700 md:col-span-2">
                      <span>🏅</span>
                      <span className="font-medium">Premios:</span>
                      <span className="text-xs">
                        {eventData.prizes[0]?.prizes?.length || 0} premios disponibles
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <h4 className="font-semibold text-yellow-900 text-sm mb-2">Modalidad de Pago</h4>
            <div className="flex items-center gap-2">
              <Badge variant={paymentData.paymentMode === 'my_part' ? 'secondary' : 'default'}>
                {paymentData.paymentMode === 'my_part' ? '💰 Pago dividido' : '💳 Pago completo'}
              </Badge>
              {paymentData.mode === 'parejas' && paymentData.paymentMode === 'my_part' && (
                <span className="text-xs text-yellow-700">
                  Se enviará notificación a tu pareja para completar el pago
                </span>
              )}
            </div>
          </div>
          
          <div className="border-t pt-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium">Total a pagar:</span>
              <span className="text-2xl font-bold text-green-600">${paymentData.amount} MXN</span>
            </div>
            {paymentData.mode === 'parejas' && paymentData.paymentMode === 'my_part' && (
              <p className="text-sm text-gray-600 mt-1">
                Tu compañero pagará la otra mitad por separado
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment Form - Stripe Checkout Info */}
      <Card className="bg-white/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Información de Pago
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <CreditCard className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm font-medium text-primary">
                Pago seguro con Stripe
              </p>
              <p className="text-xs text-primary/80">
                Serás redirigido a una página de pago segura para completar tu transacción
              </p>
            </div>
          </div>
          
          {/* Security Notice */}
          <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
            <Lock className="h-4 w-4 text-green-600" />
            <p className="text-sm text-green-800">
              Tu información de pago está protegida con encriptación SSL de nivel bancario
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Payment Button */}
      <div className="space-y-3">
        <Button 
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creando sesión de pago...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              Proceder al Pago - €{paymentData.amount}
            </div>
          )}
        </Button>
        
        {isProcessing && (
          <p className="text-sm text-center text-gray-600">
            Se abrirá una nueva ventana con la página de pago segura de Stripe
          </p>
        )}
      </div>

      {/* Terms */}
      <p className="text-xs text-gray-500 text-center">
        Al confirmar el pago, aceptas los términos y condiciones del evento y la política de cancelación.
      </p>
    </div>
  );
};

export default PaymentProcess;
